import { httpClient } from "@bw/shared/api";
import { asMoney } from "@bw/shared/types";
import type { Schema } from "@bw/shared/types/generated";

export type PlatformSettings = Schema["PlatformSettings"];
export type PlatformSettingsUpdate = Schema["PlatformSettingsUpdate"];

type RequiredPlatformSettingsField =
  | "default_commission_rate"
  | "feature_flags"
  | "maintenance_mode"
  | "initial_inquiry_message_limit"
  | "require_mfa";

/** Generated settings contract after the required response fields are validated. */
export type ValidatedPlatformSettings = PlatformSettings & {
  [Field in RequiredPlatformSettingsField]-?: NonNullable<
    PlatformSettings[Field]
  >;
};

function isBooleanMap(value: unknown): value is Record<string, boolean> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.values(value).every((entry) => typeof entry === "boolean")
  );
}

function isOptionalMoney(value: unknown): boolean {
  if (value === null || value === undefined) return true;
  if (typeof value !== "string") return false;
  try {
    return asMoney(value) !== null;
  } catch {
    return false;
  }
}

export function isPlatformSettings(
  value: unknown,
): value is ValidatedPlatformSettings {
  if (typeof value !== "object" || value === null) return false;
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.default_commission_rate === "number" &&
    Number.isFinite(candidate.default_commission_rate) &&
    isBooleanMap(candidate.feature_flags) &&
    typeof candidate.maintenance_mode === "boolean" &&
    typeof candidate.initial_inquiry_message_limit === "number" &&
    Number.isInteger(candidate.initial_inquiry_message_limit) &&
    candidate.initial_inquiry_message_limit >= 1 &&
    candidate.initial_inquiry_message_limit <= 10 &&
    isOptionalMoney(candidate.reconciliation_alert_tolerance) &&
    typeof candidate.require_mfa === "boolean"
  );
}

function requirePlatformSettings(value: unknown): ValidatedPlatformSettings {
  if (!isPlatformSettings(value)) {
    throw new Error("The platform settings response is incomplete.");
  }
  return value;
}

export async function getSettings(): Promise<ValidatedPlatformSettings> {
  const response = await httpClient.get<unknown>("/platform/settings");
  return requirePlatformSettings(response);
}

export async function updateSettings(
  settings: PlatformSettingsUpdate,
): Promise<ValidatedPlatformSettings> {
  const response = await httpClient.patch<unknown>(
    "/platform/settings",
    settings,
  );
  return requirePlatformSettings(response);
}
